import { Glyph } from "../lib/glyphs";
import { prefersReducedMotion, useReveal } from "../lib/useReveal";

interface Chip {
  x: number;
  y: number;
  w: number;
  h: number;
  glyph: string;
}

const CHIPS: Chip[] = [
  { x: 48, y: 54, w: 104, h: 70, glyph: "code" },
  { x: 340, y: 48, w: 84, h: 58, glyph: "shield" },
  { x: 330, y: 250, w: 104, h: 70, glyph: "cloud" },
  { x: 56, y: 252, w: 84, h: 58, glyph: "ai" },
  { x: 206, y: 156, w: 68, h: 54, glyph: "layers" },
];

const TRACES = [
  "M152 89 H250 V77 H340",
  "M382 106 V250",
  "M330 285 H240 V281 H140",
  "M98 252 V124 H100",
  "M206 183 H160 V89",
  "M274 183 H320 V285",
];

const PADS: Array<[number, number]> = [
  [250, 77],
  [340, 77],
  [160, 89],
  [320, 285],
  [240, 281],
  [98, 160],
];

export function CircuitBoard(): React.ReactElement {
  const { ref, shown } = useReveal(0.3);
  const reduced = prefersReducedMotion();

  return (
    <div ref={ref} className={`viz viz-circuit${shown ? " is-shown" : ""}`}>
      <svg viewBox="0 0 480 380" role="img" aria-label="Nevosoft's services interconnect like a circuit — each capability wired into the whole.">
        <defs>
          <pattern id="circuit-dots" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(120,170,255,0.16)" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="480" height="380" fill="url(#circuit-dots)" />

        {TRACES.map((d, index) => (
          <g key={d}>
            <path
              id={`ct-${index}`}
              d={d}
              pathLength={1}
              className="circuit-trace"
              style={{ strokeDashoffset: shown ? 0 : 1, transitionDelay: `${index * 90}ms` }}
            />
            {shown && !reduced && (
              <circle r="3" className="circuit-pulse">
                <animateMotion dur="2.6s" begin={`${index * 0.42}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                  <mpath href={`#ct-${index}`} />
                </animateMotion>
              </circle>
            )}
          </g>
        ))}

        {PADS.map(([x, y]) => (
          <rect key={`${x}-${y}`} x={x - 3} y={y - 3} width="6" height="6" rx="1.5" className="circuit-pad" />
        ))}

        {CHIPS.map((chip, index) => (
          <g key={chip.glyph} className="circuit-chip" style={{ transitionDelay: `${220 + index * 70}ms` }}>
            {[0, 1, 2].map((pin) => {
              const py = chip.y + (chip.h * (pin + 1)) / 4;
              return (
                <g key={pin}>
                  <line x1={chip.x - 7} y1={py} x2={chip.x} y2={py} className="circuit-pin" />
                  <line x1={chip.x + chip.w} y1={py} x2={chip.x + chip.w + 7} y2={py} className="circuit-pin" />
                </g>
              );
            })}
            <rect x={chip.x} y={chip.y} width={chip.w} height={chip.h} rx="10" className="circuit-chip-body" />
            <g className="circuit-chip-icon" transform={`translate(${chip.x + chip.w / 2} ${chip.y + chip.h / 2})`}>
              <Glyph name={chip.glyph} size={26} />
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
}
